import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, CheckCircle, XCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useAuthStore } from "../store/authStore";
import { toast } from "../components/ui/Toaster";

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [resetComplete, setResetComplete] = useState(false);

  const { resetPassword } = useAuth();
  const { isLoading } = useAuthStore();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.password) {
      newErrors.password = "Wachtwoord is vereist";
    } else if (formData.password.length < 6) {
      newErrors.password = "Wachtwoord moet minstens 6 tekens bevatten";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Bevestig je wachtwoord";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Wachtwoorden komen niet overeen";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error(
        "Ongeldige reset link",
        "Deze wachtwoord reset link is ongeldig of verlopen",
      );
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      await resetPassword(token, formData.password);
      setResetComplete(true);
      toast.success(
        "Wachtwoord reset succesvol!",
        "Je kunt nu inloggen met je nieuwe wachtwoord",
      );
    } catch (error: any) {
      console.error("Password reset failed:", error);
      toast.error(
        "Reset mislukt",
        error.message || "Probeer opnieuw of vraag een nieuwe reset link aan",
      );
    }
  };

  if (!token) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full border border-neon-pink/30 bg-neon-pink/[.08]">
            <XCircle className="h-7 w-7 text-neon-pink" />
          </div>
          <h2 className="mt-4 text-2xl font-bold text-fg-bright tracking-tight">
            Ongeldige reset link
          </h2>
          <p className="mt-2 text-sm text-fg-muted">
            Deze wachtwoord reset link is ongeldig of verlopen.
          </p>
        </div>

        <div className="text-center">
          <Link
            to="/forgot-password"
            className="sf-link text-sm"
          >
            Vraag een nieuwe reset link aan
          </Link>
        </div>
      </div>
    );
  }

  if (resetComplete) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full border border-neon-lime/25 bg-neon-lime/[.08]">
            <CheckCircle className="h-7 w-7 text-neon-lime" />
          </div>
          <h2 className="mt-4 text-2xl font-bold text-fg-bright tracking-tight">
            Wachtwoord reset succesvol
          </h2>
          <p className="mt-2 text-sm text-fg-muted">
            Je wachtwoord is succesvol bijgewerkt.
          </p>
        </div>

        <div className="text-center">
          <Link
            to="/login"
            className="sf-btn-primary inline-flex px-6 py-3.5 text-base"
          >
            Inloggen met nieuw wachtwoord
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="sf-eyebrow mb-3">// nieuw wachtwoord</div>
        <h2 className="text-2xl font-bold text-fg-bright tracking-tight">
          Reset je wachtwoord
        </h2>
        <p className="mt-2 text-sm text-fg-muted">
          Voer hieronder je nieuwe wachtwoord in.
        </p>
      </div>

      <form className="space-y-4" onSubmit={handleSubmit}>
        {/* Password field */}
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-semibold text-fg-soft"
          >
            Nieuw Wachtwoord
          </label>
          <div className="mt-1.5 relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              value={formData.password}
              onChange={handleChange}
              className={`sf-input pr-10 ${
                errors.password ? "!border-neon-pink/60" : ""
              }`}
              placeholder="Voer je nieuwe wachtwoord in"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-fg-faint" />
              ) : (
                <Eye className="h-4 w-4 text-fg-faint" />
              )}
            </button>
            {errors.password && (
              <p className="mt-1 text-sm text-neon-pink-soft">{errors.password}</p>
            )}
          </div>
        </div>

        {/* Confirm Password field */}
        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-semibold text-fg-soft"
          >
            Bevestig Nieuw Wachtwoord
          </label>
          <div className="mt-1.5 relative">
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`sf-input pr-10 ${
                errors.confirmPassword ? "!border-neon-pink/60" : ""
              }`}
              placeholder="Bevestig je nieuwe wachtwoord"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4 text-fg-faint" />
              ) : (
                <Eye className="h-4 w-4 text-fg-faint" />
              )}
            </button>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-neon-pink-soft">
                {errors.confirmPassword}
              </p>
            )}
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="sf-btn-primary w-full py-3.5 text-base"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Resetten...
              </>
            ) : (
              "Wachtwoord resetten"
            )}
          </button>
        </div>

        <div className="text-center">
          <Link
            to="/login"
            className="sf-link text-sm"
          >
            Terug naar inloggen
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ResetPasswordPage;
